import React from 'react';
import { DossierClient } from './utils/types';
import { formatDate, openEmailClient, openWhatsApp } from './utils/helpers';
import { canSeeArchiveStatus, canSeeRejectedStatus, canModifyStatus, canDeleteSubfoldersAndFiles } from './utils/permissions';

interface DocumentsTableProps {
  dossiers: DossierClient[];
  currentUserRole: string;
  onOpenDossier: (dossier: DossierClient) => void;
  onChangeStatut: (reference: string, newStatut: any) => void;
  onDelete: (dossier: DossierClient) => void;
}

const DocumentsTable: React.FC<DocumentsTableProps> = ({
  dossiers,
  currentUserRole,
  onOpenDossier,
  onChangeStatut,
  onDelete
}) => {
  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl border-2 sm:border-4 border-blue-100 overflow-hidden">
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b-2 sm:border-b-4 border-blue-200 bg-gradient-to-r from-blue-600 to-indigo-600">
        <h3 className="text-lg sm:text-xl font-bold text-white flex items-center">
          <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Documents de voyages ({dossiers.length})
        </h3>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-blue-50 to-indigo-50">
            <tr>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Référence</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">E-mail / Téléphone</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Date</th>
              <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {dossiers.map((dossier) => (
              <tr key={dossier.reference} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-white transition-all duration-300">
                <ReferenceColumn dossier={dossier} />
                <ClientColumn dossier={dossier} />
                <ContactColumn dossier={dossier} />
                <StatusColumn 
                  dossier={dossier} 
                  currentUserRole={currentUserRole}
                  onChangeStatut={onChangeStatut}
                />
                <DateColumn dossier={dossier} />
                <ActionsColumn 
                  dossier={dossier}
                  currentUserRole={currentUserRole}
                  onOpenDossier={onOpenDossier}
                  onDelete={onDelete}
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ReferenceColumn: React.FC<{ dossier: DossierClient }> = ({ dossier }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
    <div className="flex items-center">
      <div className={`w-8 h-8 sm:w-10 sm:h-10 ${dossier.typeDocument === 'voyage' ? 'bg-blue-100' : 'bg-purple-100'} rounded-full flex items-center justify-center mr-2 sm:mr-3`}>
        <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${dossier.typeDocument === 'voyage' ? 'text-blue-600' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {dossier.typeDocument === 'voyage' ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          )}
        </svg>
      </div>
      <div>
        <p className="text-sm font-bold text-gray-900 truncate max-w-[120px] sm:max-w-none">{dossier.reference}</p>
        <p className="text-xs text-gray-500">
          {dossier.typeDocument === 'voyage' ? 'Voyage' : 'Transfert'}
        </p>
      </div>
    </div>
  </td>
);

const ClientColumn: React.FC<{ dossier: DossierClient }> = ({ dossier }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
    <div>
      <p className="text-sm font-medium text-gray-900 truncate max-w-[100px] sm:max-w-none">{dossier.nom} {dossier.prenom}</p>
      <p className="text-xs text-gray-500 truncate max-w-[100px] sm:max-w-none">{dossier.profession}</p>
      <p className="text-xs text-gray-400">{dossier.sexe === 'H' ? 'Homme' : 'Femme'}</p>
    </div>
  </td>
);

const ContactColumn: React.FC<{ dossier: DossierClient }> = ({ dossier }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
    <div className="flex flex-col space-y-1">
      <button
        onClick={() => openEmailClient(dossier.email)}
        className="text-blue-600 hover:text-blue-800 hover:underline hover:bg-blue-50 px-2 py-1 rounded transition-all duration-300 text-sm flex items-center"
      >
        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <span className="truncate max-w-[120px]">{dossier.email}</span>
      </button>
      {dossier.telephone && (
        <button
          onClick={() => openWhatsApp(dossier.telephone || '', dossier.nom, dossier.prenom, 'voyage', dossier.reference, dossier.motif || 'votre dossier')}
          className="text-green-600 hover:text-green-800 hover:bg-green-50 px-2 py-1 rounded transition-all duration-300 text-xs flex items-center"
        >
          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="truncate max-w-[100px]">{dossier.telephone}</span>
        </button>
      )}
    </div>
  </td>
);

const StatusColumn: React.FC<{ 
  dossier: DossierClient; 
  currentUserRole: string;
  onChangeStatut: (reference: string, newStatut: any) => void;
}> = ({ dossier, currentUserRole, onChangeStatut }) => {
  const getStatusClasses = (statut: string) => {
    switch (statut) {
      case 'en_attente': return 'bg-yellow-100 text-yellow-800 focus:ring-yellow-500';
      case 'partiellement_apuré': return 'bg-orange-100 text-orange-800 focus:ring-orange-500';
      case 'apuré': return 'bg-green-100 text-green-800 focus:ring-green-500';
      case 'archivé': return 'bg-blue-100 text-blue-800 focus:ring-blue-500';
      case 'rejeté': return 'bg-red-600 text-white focus:ring-red-500';
      default: return 'bg-gray-100 text-gray-800 focus:ring-gray-500';
    }
  };

  return (
    <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
      <select
        value={dossier.statut}
        onChange={(e) => onChangeStatut(dossier.reference, e.target.value as any)}
        disabled={!canModifyStatus(currentUserRole)}
        className={`text-xs px-2 sm:px-3 py-1 rounded-full border-0 font-medium focus:ring-2 transition-all cursor-pointer ${getStatusClasses(dossier.statut)}`}
      >
        <option value="en_attente">En attente</option>
        <option value="partiellement_apuré">Partiellement apuré</option>
        <option value="apuré">Apuré</option>
        {canSeeArchiveStatus(currentUserRole) && <option value="archivé">Archivé</option>}
        {canSeeRejectedStatus(currentUserRole) && <option value="rejeté">Rejeté</option>}
      </select>
    </td>
  );
};

const DateColumn: React.FC<{ dossier: DossierClient }> = ({ dossier }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
    <div>
      <p>{formatDate(dossier.dateCreation)}</p>
      <p className="text-xs text-gray-400">
        {dossier.sousDossiers.length} sous-dossier(s)
      </p>
    </div>
  </td>
);

const ActionsColumn: React.FC<{ 
  dossier: DossierClient;
  currentUserRole: string;
  onOpenDossier: (dossier: DossierClient) => void;
  onDelete: (dossier: DossierClient) => void;
}> = ({ dossier, currentUserRole, onOpenDossier, onDelete }) => (
  <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
    <div className="flex justify-end space-x-1 sm:space-x-2 flex-wrap gap-1">
      <button
        onClick={() => onOpenDossier(dossier)}
        className="text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 sm:px-3 py-1 rounded-lg transition-all duration-300 text-xs sm:text-sm"
      >
        Voir
      </button>
      {(currentUserRole === 'admin_bank' || currentUserRole === 'superviseur') && dossier.statut !== 'rejeté' && (
        <button
          onClick={() => onDelete(dossier)}
          className="text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-2 sm:px-3 py-1 rounded-lg transition-all duration-300 text-xs sm:text-sm"
        >
          Supprimer
        </button>
      )}
    </div>
  </td>
);

export default DocumentsTable;
